import { IconButton } from '@lastbrand/web-ui/icon-button'
import { SaveIcon, SaveSolidIcon } from '@lastbrand/web-ui/icons'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useCallback, useState, useSyncExternalStore } from 'react'

import { useProductWishList } from '@src/hooks/useProductWishlist'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import wishlistStateManager from '@src/quince-site-common/src/redux/globalStateManager/WishlistStateManager'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

import type { AddToWishlistButtonProps } from './add-to-wishlist-button.types'

const noopUnsubscribe = () => undefined

// Both hearts share one state ramp — default `neutral/black`, hover `neutral/gray`, pressed
// `neutral/mediumgray` (Figma `.ProductTileButtonFavorite`, node 807:9097). On the solid heart
// this paints the fill; on the outline heart, the stroke.
const ACTIVE_HEART_CSS = css.raw({
  color: 'black',
  _hover: { color: 'gray', bg: 'transparent' },
  _active: { color: 'mediumgray', bg: 'transparent' },
})

// Spelled out rather than spread from ACTIVE_HEART_CSS: Panda extracts styles statically, and
// a css.raw() spread into another css.raw() is not reliably resolved at build time.
const INACTIVE_HEART_CSS = css.raw({
  color: 'black',
  // The outline heart adds a 70%-white interior fill (`custom/white-trans-70`) so the
  // stroke keeps contrast over light product imagery — an accessibility fix raised in
  // STRFRNT-15769, which also raised this fill from the previous 30%.
  '& svg': { fill: 'white-trans-70' },
  _hover: { color: 'gray', bg: 'transparent' },
  _active: { color: 'mediumgray', bg: 'transparent' },
})

export function AddToWishlistButton({
  spid,
  selectedColor,
  wishlistTrackConfig,
  wishlistEnabled = true,
  hideWishlistIcon = false,
  productName,
  wishlistAriaLabel,
  wishlistAriaLabelSelected,
  tileVariant = 'default',
  wishlistButtonCss,
  wishlistDisabled,
  syncAcrossInstances = true,
}: AddToWishlistButtonProps) {
  const { t } = useTranslation('common')
  const { isNativeView } = useDeviceInfo()
  const isLoggedIn = isBrowser() ? AuthUtil.isLoggedIn() : false
  const { isItemInWishList, addItemToWishList, removeItemFromWishList } = useProductWishList()

  // Read the wishlist as an external store instead of mirroring it into local state, so the heart
  // stays correct across login hydration and DS-grid remounts (no empty-heart flash). `syncAcrossInstances=false`
  // drops the store subscription entirely (no-op unsubscribe) — this instance ignores *all* later store
  // publishes, both sibling toggles and async login hydration, matching legacy `ProductCardAddToWishlist`
  // parity. `getSnapshot` still runs correctly on its own renders (its own click, a remount, etc).
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      syncAcrossInstances ? wishlistStateManager.subscribe(onStoreChange) : noopUnsubscribe,
    [syncAcrossInstances],
  )
  const storedWishlisted = useSyncExternalStore(
    subscribe,
    () => isItemInWishList(spid, selectedColor),
    () => false,
  )

  // Optimistic override held only while a toggle is in flight, then handed back to the store truth.
  const [pending, setPending] = useState<boolean | null>(null)
  const isWishlisted = pending ?? storedWishlisted

  const resolvedAriaLabel = wishlistAriaLabel ?? t('pc_add_wishlist_aria', { productName: productName ?? '' })
  const resolvedAriaLabelSelected =
    wishlistAriaLabelSelected ?? t('pc_remove_wishlist_aria', { productName: productName ?? '' })

  const isMini = tileVariant === 'mini'
  const isCompact = tileVariant === 'compact'
  const isNarrow = isCompact || isMini

  const showWishlist = wishlistEnabled && !(isNativeView && !isLoggedIn) && !hideWishlistIcon

  const onWishlistToggle = useCallback(() => {
    const next = !isWishlisted
    setPending(next)
    const op = next
      ? addItemToWishList(spid, selectedColor, wishlistTrackConfig ?? {})
      : removeItemFromWishList(spid, selectedColor, wishlistTrackConfig ?? {})
    op.catch((e: unknown) => {
      console.error(e)
    }).finally(() => {
      setPending(null)
    })
  }, [addItemToWishList, isWishlisted, removeItemFromWishList, selectedColor, spid, wishlistTrackConfig])

  if (!showWishlist) return null

  return (
    <div
      className={css({
        position: 'absolute',
        top: { base: 'space-8', lg: isNarrow ? 'space-8' : 'space-16' },
        right: { base: 'space-8', lg: isNarrow ? 'space-8' : 'space-16' },
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      })}
    >
      <IconButton
        type="button"
        variant="ghost"
        aria-label={isWishlisted ? resolvedAriaLabelSelected : resolvedAriaLabel}
        aria-pressed={isWishlisted}
        onClick={onWishlistToggle}
        disabled={wishlistDisabled}
        css={{
          flexShrink: 0,
          bg: 'transparent',
          ...(isWishlisted ? ACTIVE_HEART_CSS : INACTIVE_HEART_CSS),
          ...wishlistButtonCss,
        }}
      >
        {isWishlisted ? <SaveSolidIcon width={20} height={20} /> : <SaveIcon width={20} height={20} />}
      </IconButton>
    </div>
  )
}
